import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import logo from '../images/angel_studios_logo.png'

const AboutUs = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    const statsVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    return (
        <motion.div
            className="min-h-screen bg-black text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
        >
            {/* Navigation */}
            <motion.nav
                className="relative z-20 p-2 px-6"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
            >
                <Link
                    to="/"
                    className="flex items-center text-gray-400 hover:text-white transition-colors duration-300"
                >
                    {/* <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Home */}
                    <motion.img
                        src={logo}
                        className='h-10 max-md:h-10  filter brightness-0 invert my-2'
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    />
                </Link>
            </motion.nav>

            {/* Hero Section */}
            <motion.section
                className="relative py-2 overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-red-900/10 via-transparent to-purple-900/10 z-0" />

                <div className="relative z-10 text-center max-w-4xl mx-auto px-2">
                    <motion.div
                        className="mb-8"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        <h1 className="text-6xl max-md:text-4xl font-bold bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent mb-6">
                            ABOUT US
                        </h1>
                        <div className="w-32 h-1 bg-gradient-to-r from-red-600 to-orange-500 mx-auto mb-8"></div>
                    </motion.div>

                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-light text-gray-300 mb-4">
                            Angels Studios
                        </h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Building worlds that redefine entertainment
                        </p>
                    </motion.div>
                </div>
            </motion.section>

            {/* Main Content */}
            <motion.div
                className="max-w-6xl mx-auto px-4 py-16"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Introduction Section */}
                <motion.section
                    className="mb-16"
                    variants={itemVariants}
                >
                    <div className="bg-gradient-to-br from-gray-900 to-black p-8 md:p-12 rounded-2xl border border-gray-800 shadow-2xl">
                        <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-8 text-center">
                            We are <span className="text-white font-semibold">Angels Studios</span>, a collective of Hollywood veterans who've spent decades building other people's worlds—now we're building our own.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8 mt-12">
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                                    <h3 className="text-2xl font-bold text-white mb-4">Our Heritage</h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        Our core team has contributed to more than 30 global blockbusters, including films within the Marvel Cinematic Universe, Avatar, The Lord of the Rings franchise, Top Gun: Maverick, and many more.
                                    </p>
                                </div>
                            </motion.div>

                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                                    <h3 className="text-2xl font-bold text-white mb-4">Our Expertise</h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        We understand how tentpole stories are made—from the first spark on the page to the last pixel on screen.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.section>

                {/* Mission Section */}
                <motion.section
                    className="mb-16"
                    variants={itemVariants}
                >
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-4">
                            Our Mission
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-orange-500 mx-auto"></div>
                    </div>

                    <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border-l-4 border-red-500 p-8 rounded-r-2xl">
                        <p className="text-2xl md:text-3xl text-gray-200 leading-relaxed text-center italic">
                            "Backed by a network of visionary Silicon Valley investors, we sit at the collision point of cinema and frontier technology."
                        </p>
                    </div>

                    <motion.div
                        className="mt-8 bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-gray-800"
                        whileHover={{ scale: 1.01 }}
                        transition={{ type: "spring", stiffness: 400 }}
                    >
                        <p className="text-xl text-gray-300 leading-relaxed text-center">
                            Our mission is bold and brutally simple: produce <span className="text-white font-bold">30–40 fully AI-driven episodes every year</span>—stories designed from day one to live across streaming platforms, games, and immersive experiences.
                        </p>
                    </motion.div>
                </motion.section>

                {/* Market Opportunity */}
                <motion.section
                    className="mb-16"
                    variants={itemVariants}
                >
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-4">
                            The Future is Now
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-orange-500 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <motion.div
                            className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 p-8 rounded-2xl border border-purple-800"
                            whileHover={{ scale: 1.02 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <h3 className="text-2xl font-bold text-white mb-4">Market Opportunity</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                The timing couldn't be better. Analysts estimate that AI in media and entertainment will surge to well over...
                            </p>
                            <div className="text-3xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text">
                                $100B+
                            </div>
                            <p className="text-gray-400 text-sm mt-2">annually by 2030</p>
                        </motion.div>

                        <motion.div
                            className="bg-gradient-to-br from-green-900/20 to-teal-900/20 p-8 rounded-2xl border border-green-800"
                            whileHover={{ scale: 1.02 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <h3 className="text-2xl font-bold text-white mb-4">Growth Potential</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                With some forecasts pushing beyond...
                            </p>
                            <div className="text-3xl font-bold text-transparent bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text">
                                $200B
                            </div>
                            <p className="text-gray-400 text-sm mt-2">as AI transforms entertainment worldwide</p>
                        </motion.div>
                    </div>

                    <motion.div
                        className="mt-8 bg-gray-800/50 p-6 rounded-xl border border-gray-700"
                        variants={itemVariants}
                    >
                        <p className="text-lg text-gray-300 text-center">
                            AI will transform how films and episodic content is written, produced, localized, and experienced worldwide.
                        </p>
                    </motion.div>
                </motion.section>

                {/* Vision Section */}
                <motion.section
                    className="mb-16"
                    variants={itemVariants}
                >
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-4">
                            Our Vision
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-orange-500 mx-auto"></div>
                    </div>

                    <motion.div
                        className="bg-gradient-to-br from-gray-900 to-black p-12 rounded-2xl border border-gray-800 text-center relative overflow-hidden"
                        whileHover={{ scale: 1.01 }}
                        transition={{ type: "spring", stiffness: 400 }}
                    >
                        {/* Animated background elements */}
                        <div className="absolute top-0 left-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl"></div>

                        <p className="text-2xl md:text-3xl text-gray-200 leading-relaxed mb-6">
                            Our dream is to make <span className="text-white font-bold">The Angels Universe</span> the flagship IP of this new era
                        </p>

                        <motion.div
                            className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent my-8 pb-2"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 1, duration: 0.8 }}
                        >
                            Proving that AI doesn't replace human imagination; it amplifies it.
                        </motion.div>
                    </motion.div>
                </motion.section>

                {/* Stats Section */}
                <motion.section
                    className="mb-16"
                    variants={containerVariants}
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { number: "30+", label: "Global Blockbusters" },
                            { number: "30-40", label: "AI Episodes Per Year" },
                            { number: "$100B+", label: "Market by 2030" },
                            { number: "∞", label: "Creative Possibilities" }
                        ].map((stat, index) => (
                            <motion.div
                                key={index}
                                className="text-center p-6 bg-gray-800/50 rounded-xl border border-gray-700"
                                variants={statsVariants}
                                whileHover={{ scale: 1.05, y: -5 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <div className="text-2xl md:text-3xl font-bold text-transparent bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-gray-400 text-sm">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>
            </motion.div>

            {/* CTA Section */}
            <motion.section
                className="py-16 bg-gradient-to-b from-gray-900 to-black border-t border-gray-800"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
            >
                <div className="max-w-4xl mx-auto text-center px-4">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Join the Revolution
                    </h2>
                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                        Be part of the next generation of storytelling where technology meets imagination.
                    </p>
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Link
                            to="/"
                            className="inline-block bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-600 hover:to-orange-500 text-white font-bold py-4 px-12 rounded-full text-lg transition-all duration-300 shadow-2xl hover:shadow-red-500/25"
                        >
                            Explore The Angels Universe
                        </Link>
                    </motion.div>
                </div>
            </motion.section>
        </motion.div>
    );
};

export default AboutUs;